/**
 * Attendance and expenses — port of `features/staff_ops/staff_ops_strings.dart`.
 * Keys and wording are kept identical to the Flutter table so the two stay in step;
 * Telugu entries are optional per key (fallback: English).
 */
import { l10nTable } from './l10n';

export const staffOpsStrings = l10nTable(
    {
        'moduleTitle': 'Attendance and expenses (manager on hub, owner on cloud)',

        // Attendance
        'attendanceTitle': 'Attendance',
        'attendanceSub': 'today · per shift',
        'attSummary': '{p} present · {a} absent · {u} not marked',
        'attIntro': '{summary}. Tap a status to cycle present → absent → week off → leave → holiday.',
        'loginIn': '{shift} · in {time}',
        'noLogin': '{shift} · no login',
        'manualMark': 'manual',
        'statusPresent': 'Present',
        'statusAbsent': 'Absent',
        'statusWeekOff': 'Week off',
        'statusPaidLeave': 'Paid leave',
        'statusUnpaidLeave': 'Unpaid leave',
        'statusHoliday': 'Holiday',
        'statusUnmarked': 'Not marked',
        'attNoteHub': 'Attendance is logged on the hub for the business day and feeds Owner → Salary & payroll. It can be corrected until day close.',
        'attNoteCloud': 'Marks saved here win over hub logins and feed Salary & payroll straight away.',
        'saveAttendance': 'Save attendance',
        'saveChanges': 'Save {n} changes',
        'attendanceSaved': 'Attendance saved',
        'noStaffTitle': 'No staff shifts yet',
        'noStaffBody': 'The owner defines shifts and staff in the cloud; they reach the hub at the next sync.',

        // Expense log
        'expensesTitle': 'Expenses',
        'expensesSub': 'logged today',
        'kpiToday': 'Today',
        'kpiDrawer': 'From drawer',
        'kpiEntries': 'Entries today',
        'kpiMonth': 'This month',
        'kpiMonthHub': 'in the Owner app',
        'today': 'Today',
        'noExpensesTitle': 'No expenses logged today',
        'noExpensesBody': 'Gas, vegetables bought in cash, a repair — log it so the drawer and P&L stay right.',
        'byWho': 'by {who} · {time}',
        'payCashDrawer': 'Cash · drawer',
        'payCash': 'Cash',
        'payUpi': 'UPI',
        'payCard': 'Card',
        'payBank': 'Bank transfer',
        'payCheque': 'Cheque',
        'payCredit': 'Credit · pay later',
        'void': 'Void',
        'voided': 'VOID · {reason}',
        'salary': 'Salary',
        'voidTitle': 'Void expense {amount}',
        'voidHint': 'Reason (a drawer-paid entry puts the cash back)',
        'cancel': 'Cancel',
        'expenseVoided': 'Expense voided',
        'expNote': 'Managers and the owner can both log expenses. Cash entries reduce the drawer at cash-up; everything flows into the P&L by expense type.',
        'logExpense': 'Log expense',

        // New expense
        'newExpenseTitle': 'Log expense',
        'newExpenseSub': 'today · {where}',
        'whereHub': 'saved on the hub',
        'whereCloud': 'saved to cloud',
        'expenseType': 'Expense type',
        'typeRestricted': 'Restricted type — only the staff chosen by the owner see it.',
        'typeOpen': 'Visible to every manager and the owner.',
        'noCategories': 'No expense types yet. The owner adds them in Expenses → Types.',
        'amount': 'Amount',
        'paidBy': 'Paid by',
        'noteVendor': 'Note / vendor',
        'noteHint': 'e.g. Rythu bazar, 14 kg',
        'attachBill': 'Attach bill photo',
        'attachGap': 'Bill photos are not supported on this device yet.',
        'creditNote': 'Credit is saved as payment mode OTHER (not paid from the drawer).',
        'approvalNote': 'Entries above ₹10,000 are held for owner approval before they hit the P&L.',
        'saveExpense': 'Save expense',
        'savedTitle': 'Expense logged',
        'savedHub': "Saved on the hub against today's business day{drawer}. It appears in the P&L under its expense type.",
        'savedDrawer': ' and deducted from the cash drawer',
        'savedCloud': 'Saved to the cloud for today. It appears in the P&L under its expense type.',
        'backToExpenses': 'Back to expenses',
    },
    {
        'moduleTitle': 'హాజరు, చెల్లించిన డబ్బు (హబ్‌లో మేనేజర్, క్లౌడ్‌లో యజమాని)',
        'attendanceTitle': 'హాజరు',
        'attendanceSub': 'ఈరోజు · షిఫ్ట్ వారీగా',
        'attSummary': '{p} మంది వచ్చారు · {a} మంది రాలేదు · {u} మందికి గుర్తు పెట్టలేదు',
        'attIntro': '{summary}. మార్చడానికి స్టేటస్‌పై నొక్కండి: వచ్చారు → రాలేదు → వారపు సెలవు → సెలవు → పండుగ సెలవు.',
        'loginIn': '{shift} · వచ్చిన సమయం {time}',
        'noLogin': '{shift} · లాగిన్ లేదు',
        'manualMark': 'చేతితో గుర్తు',
        'statusPresent': 'వచ్చారు',
        'statusAbsent': 'రాలేదు',
        'statusWeekOff': 'వారపు సెలవు',
        'statusPaidLeave': 'జీతంతో సెలవు',
        'statusUnpaidLeave': 'జీతం లేని సెలవు',
        'statusHoliday': 'పండుగ సెలవు',
        'statusUnmarked': 'గుర్తు పెట్టలేదు',
        'attNoteHub': 'హాజరు ఆ రోజు లెక్కకు హబ్‌లో సేవ్ అవుతుంది, యజమాని → జీతాలు లోకి వెళ్తుంది. రోజు మూసే వరకు సరిచేయవచ్చు.',
        'attNoteCloud': 'ఇక్కడ సేవ్ చేసిన హాజరే లెక్కలోకి వస్తుంది, హబ్ లాగిన్ కాదు. ఇది వెంటనే జీతాలు లోకి వెళ్తుంది.',
        'saveAttendance': 'హాజరు సేవ్ చేయండి',
        'saveChanges': '{n} మార్పులు సేవ్ చేయండి',
        'attendanceSaved': 'హాజరు సేవ్ అయింది',
        'noStaffTitle': 'ఇంకా సిబ్బంది షిఫ్ట్‌లు లేవు',
        'noStaffBody': 'షిఫ్ట్‌లు, సిబ్బందిని యజమాని క్లౌడ్‌లో పెడతారు; తర్వాతి సింక్‌లో అవి హబ్‌కు వస్తాయి.',
        'expensesTitle': 'చెల్లించిన డబ్బు',
        'expensesSub': 'ఈరోజు రాసినవి',
        'kpiToday': 'ఈరోజు',
        'kpiDrawer': 'క్యాష్ డ్రాయర్ నుంచి',
        'kpiEntries': 'ఈరోజు ఎంట్రీలు',
        'kpiMonth': 'ఈ నెల',
        'kpiMonthHub': 'యజమాని యాప్‌లో',
        'today': 'ఈరోజు',
        'noExpensesTitle': 'ఈరోజు ఏ చెల్లింపూ రాయలేదు',
        'noExpensesBody': 'గ్యాస్, క్యాష్‌తో కొన్న కూరగాయలు, రిపేర్ — ఇక్కడ రాయండి. అప్పుడే క్యాష్ డ్రాయర్, లాభ నష్టాల లెక్క సరిగ్గా ఉంటాయి.',
        'byWho': '{who} రాశారు · {time}',
        'payCashDrawer': 'క్యాష్ · డ్రాయర్',
        'payCash': 'క్యాష్',
        'payUpi': 'UPI',
        'payCard': 'కార్డ్',
        'payBank': 'బ్యాంక్ ట్రాన్స్‌ఫర్',
        'payCheque': 'చెక్',
        'payCredit': 'అప్పు · తర్వాత చెల్లిస్తాం',
        'void': 'రద్దు',
        'voided': 'రద్దు అయింది · {reason}',
        'salary': 'జీతం',
        'voidTitle': '{amount} చెల్లింపు రద్దు చేయండి',
        'voidHint': 'కారణం (డ్రాయర్ నుంచి ఇచ్చినదైతే క్యాష్ తిరిగి కలుస్తుంది)',
        'cancel': 'వద్దు',
        'expenseVoided': 'చెల్లింపు రద్దు అయింది',
        'expNote': 'మేనేజర్లు, యజమాని ఇద్దరూ చెల్లించిన డబ్బు రాయవచ్చు. క్యాష్‌లో ఇచ్చినది రోజు చివర క్యాష్ లెక్కలో డ్రాయర్ నుంచి తగ్గుతుంది; అన్నీ రకం ప్రకారం లాభ నష్టాల లెక్కలోకి వెళ్తాయి.',
        'logExpense': 'చెల్లించిన డబ్బు రాయండి',
        'newExpenseTitle': 'చెల్లించిన డబ్బు రాయండి',
        'newExpenseSub': 'ఈరోజు · {where}',
        'whereHub': 'హబ్‌లో సేవ్ అవుతుంది',
        'whereCloud': 'క్లౌడ్‌లో సేవ్ అవుతుంది',
        'expenseType': 'దేనికి చెల్లించారు',
        'typeRestricted': 'ఇది కొందరికే కనిపించే రకం — యజమాని ఎంచుకున్న సిబ్బందికే కనిపిస్తుంది.',
        'typeOpen': 'ప్రతి మేనేజర్‌కు, యజమానికి కనిపిస్తుంది.',
        'noCategories': 'ఇంకా రకాలు లేవు. యజమాని చెల్లించిన డబ్బు → రకాలు లో వాటిని జోడిస్తారు.',
        'amount': 'ఎంత డబ్బు',
        'paidBy': 'ఎలా చెల్లించారు',
        'noteVendor': 'గమనిక / ఎవరికి ఇచ్చారు',
        'noteHint': 'ఉదా. రైతు బజార్, 14 కేజీలు',
        'attachBill': 'బిల్లు ఫోటో జోడించండి',
        'attachGap': 'ఈ ఫోన్‌లో బిల్లు ఫోటోలు ఇంకా పెట్టలేరు.',
        'creditNote': 'అప్పు OTHER అనే చెల్లింపు పద్ధతిగా సేవ్ అవుతుంది (డ్రాయర్ నుంచి ఇవ్వలేదు).',
        'approvalNote': '₹10,000 కంటే ఎక్కువ ఉంటే యజమాని ఒప్పుకునే వరకు ఆగుతుంది. ఆ తర్వాతే లాభ నష్టాల లెక్కలోకి వెళ్తుంది.',
        'saveExpense': 'చెల్లింపు సేవ్ చేయండి',
        'savedTitle': 'చెల్లింపు సేవ్ అయింది',
        'savedHub': 'ఈరోజు లెక్కలో హబ్‌లో సేవ్ అయింది{drawer}. ఇది లాభ నష్టాల లెక్కలో దాని రకం కింద కనిపిస్తుంది.',
        'savedDrawer': ', క్యాష్ డ్రాయర్ నుంచి తగ్గింది',
        'savedCloud': 'ఈరోజు లెక్కలో క్లౌడ్‌లో సేవ్ అయింది. ఇది లాభ నష్టాల లెక్కలో దాని రకం కింద కనిపిస్తుంది.',
        'backToExpenses': 'చెల్లించిన డబ్బుకు వెనక్కి',
    },
);
